import { useEffect, useState } from "react";
import { RefreshCw, Save, Sparkles } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { getTelephonySettings, saveTelephonySettings, type TelephonyStatus } from "../../../../lib/calls";
import { getPilotChecklist, savePilotChecklist } from "../../../../lib/callPilot";
import { forgetCallWindowPrefs } from "../../../../lib/callWindowPrefs";
import { Btn, StatusPill } from "../../../ui";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Field, FieldGroup, Note, Pill, SaveRow, ToggleRow } from "../panel/fields";
import { PilotChecklist, toDraft, type Draft } from "./PilotChecklist";

/**
 * Settings -> Integrations -> the live call AI.
 *
 * Three things live here, and the second is the reason this is a page rather
 * than a switch. The first is the switch itself, off by default, with the window
 * length and a ceiling on how many windows one call may spend. The second is the
 * CHECKLIST in `PilotChecklist`: what we want to find out on every sales call.
 * The third is how the call window OPENS - filling the screen or as a card, with
 * the panel out or shut - which belongs here rather than in a display setting
 * nobody would look for, because the only reason to want the big window is to
 * read what this card produces.
 *
 * One save button for all of it, because it is one decision - saving the switch
 * and leaving the questions behind is not a state anybody wants.
 */
export function PilotCard() {
  const t = useT();
  const [status, setStatus] = useState<TelephonyStatus | null>(null);
  const [items, setItems] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const [enabled, setEnabled] = useState(false);
  const [windowSec, setWindowSec] = useState("15");
  const [maxWindows, setMaxWindows] = useState("120");
  const [allowWithoutNotice, setAllowWithoutNotice] = useState(false);
  const [fullscreen, setFullscreen] = useState(true);
  const [panelOpen, setPanelOpen] = useState(true);

  function hydrate(data: TelephonyStatus) {
    setStatus(data);
    setEnabled(data.pilot.enabled);
    setWindowSec(String(data.pilot.windowSec));
    setMaxWindows(String(data.pilot.maxWindows));
    setAllowWithoutNotice(data.pilot.allowWithoutNotice);
    setFullscreen(data.window.fullscreen);
    setPanelOpen(data.window.pilotOpen);
  }

  useEffect(() => {
    Promise.all([getTelephonySettings(), getPilotChecklist()])
      .then(([settings, checklist]) => {
        hydrate(settings);
        setItems(checklist.items.map(toDraft));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      const [settings, checklist] = await Promise.all([
        saveTelephonySettings({
          pilot: {
            enabled,
            windowSec: Number(windowSec),
            maxWindows: Number(maxWindows),
            allowWithoutNotice,
          },
          window: { fullscreen, pilotOpen: panelOpen },
        }),
        savePilotChecklist(items.map(({ id, question, why, weight }) => ({ id, question, why, weight }))),
      ]);
      hydrate(settings);
      // Every open CRM tab caches the window defaults for the life of the tab,
      // and this is the one moment they can have changed. Only THIS tab is
      // reachable from here; the others pick it up when they next reload, which
      // is the right trade for a setting that decides how big a box opens.
      forgetCallWindowPrefs();
      // Re-keyed from what the server stored: it assigns the ids for new rows
      // and drops the questionless ones, and the panel has to show what was
      // really saved rather than what it hoped for.
      setItems(checklist.items.map(toDraft));
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  if (loading) return <PanelLoader />;

  // The server's own answer rather than a key flag worked out here. WHICH key
  // the live pass needs depends on the transcription provider, and that belongs
  // in one place - see `providers.ts`.
  const keyMissing = status?.pilot.needsKey ?? false;
  // Heard but not read back. A separate line because it is a separate state:
  // the transcript arrives and the suggestion column stays empty.
  const coachMissing = (status?.pilot.noCoach ?? false) && !keyMissing;
  // Audio that reaches a model is processing, and the person on the other end
  // has to have been told. The server refuses the windows either way, so saying
  // it here is the difference between a fixable page and a panel that is quietly
  // off on the next call.
  const noticeMissing = status?.pilot.blocker === "no_notice";
  // The same situation, with the exemption switched on: the co-pilot IS running
  // and nothing is being announced. Not a blocker, and still worth a line - a
  // waiver that goes quiet is a waiver nobody remembers granting.
  const noticeWaived = status?.pilot.blocker === "no_notice_waived";

  return (
    <IntegrationCard
      icon={Sparkles}
      title={t("settings.pilot.title")}
      desc={t("settings.pilot.desc")}
      status={
        <ConnStatus
          ok={status?.pilot.ready ?? false}
          okLabel={t("settings.pilot.statusOn")}
          offLabel={t("settings.pilot.statusOff")}
        />
      }
    >
      <form onSubmit={handleSave} className="space-y-5">
        <Note tone="info">{t("settings.pilot.about")}</Note>
        {/* Shown to everybody, not only to an ElevenLabs install: the choice a
            few centimetres away now decides how well this hears and whether it
            knows who spoke, which is a reason to make the choice rather than a
            footnote for people who already made it. */}
        <Note tone="info">{t("settings.pilot.followsProvider")}</Note>
        {/* Said plainly rather than left for the operator to work out from a
            panel that stays quiet on the next call. */}
        {keyMissing ? <Note tone="warn">{t("settings.pilot.needsKey")}</Note> : null}
        {coachMissing ? <Note tone="warn">{t("settings.pilot.noCoach")}</Note> : null}
        {noticeMissing ? <Note tone="bad">{t("settings.pilot.needsNotice")}</Note> : null}
        {noticeWaived ? <Note tone="warn">{t("settings.pilot.noticeWaived")}</Note> : null}
        {/* Not about the co-pilot at all, and shown here because this is the
            page an operator opens when they are thinking about what is done
            with a call. Recording without a notice is a problem that survives
            switching the AI off, and nothing else on this page would say so. */}
        {status?.recordsWithoutNotice ? (
          <Note tone="bad">{t("settings.pilot.recordsWithoutNotice")}</Note>
        ) : null}

        <ToggleRow
          label={t("settings.pilot.enable")}
          hint={t("settings.pilot.enableHint")}
          checked={enabled}
          onChange={setEnabled}
        />

        {/* Directly under the switch it modifies, and phrased as the exception
            it is. The hint carries the obligation rather than the mechanism:
            no code can read a sentence for its meaning, so this switch moves a
            decision from the software to the person making it. */}
        <ToggleRow
          label={t("settings.pilot.allowWithoutNotice")}
          hint={t("settings.pilot.allowWithoutNoticeHint")}
          checked={allowWithoutNotice}
          onChange={setAllowWithoutNotice}
        />

        {/* Both hold two or three digits, so both wear a pill the width of the
            answer rather than the width of the column. A 400px box around "15"
            reads as a field that expects a sentence. */}
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          <Field label={t("settings.pilot.windowSec")} hint={t("settings.pilot.windowSecHint")}>
            <Pill
              className="h-10 max-w-[120px]"
              value={windowSec}
              onChange={setWindowSec}
              inputMode="numeric"
              placeholder="15"
            />
          </Field>
          <Field label={t("settings.pilot.maxWindows")} hint={t("settings.pilot.maxWindowsHint")}>
            <Pill
              className="h-10 max-w-[120px]"
              value={maxWindows}
              onChange={setMaxWindows}
              inputMode="numeric"
              placeholder="120"
            />
          </Field>
        </div>

        {/* How the window OPENS, not what it can do. Both are defaults for a
            browser that has never been asked - the buttons in the call window
            itself always win, and they have to, or an operator who shrank the
            window would find it big again on the next call. */}
        <FieldGroup title={t("settings.pilot.windowGroup")}>
          <Note tone="info">{t("settings.pilot.windowAbout")}</Note>
          <ToggleRow
            label={t("settings.pilot.fullscreen")}
            hint={t("settings.pilot.fullscreenHint")}
            checked={fullscreen}
            onChange={setFullscreen}
          />
          <ToggleRow
            label={t("settings.pilot.panelOpen")}
            hint={t("settings.pilot.panelOpenHint")}
            checked={panelOpen}
            onChange={setPanelOpen}
          />
        </FieldGroup>

        <PilotChecklist items={items} onChange={setItems} />

        <SaveRow>
          <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
            {saveState === "saving" ? <RefreshCw className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
            {saveState === "saving" ? t("settings.pilot.saving") : t("settings.pilot.save")}
          </Btn>
          {saveState === "saved" ? <StatusPill tone="ok">{t("settings.pilot.saved")}</StatusPill> : null}
          {saveState === "error" ? <StatusPill tone="lt">{t("settings.pilot.saveError")}</StatusPill> : null}
        </SaveRow>
      </form>
    </IntegrationCard>
  );
}
